// import './theme.css'; THIS FOR TAILWIND
// Import CSS variables FIRST before any components
import './styles/lincd_variables.css';
import './styles/variables-components.css';
import './styles/variables-theme.css';

import { ProvideAuth } from 'lincd-auth/hooks/useAuth';
import { CapacitorProvider } from 'lincd-capacitor/hooks/useCapacitor';
import { AppRoot } from 'lincd-server-utils/components/AppRoot';
import { Body } from 'lincd-server-utils/components/Body';
import { Head } from 'lincd-server-utils/components/Head';
import React, { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Error } from './components/atoms/Error.js';
import { Spinner } from './components/atoms/Spinner.js';
import AppRoutes, { ROUTES } from './routes.js';

import style from './App.module.css';

//import our variable files last, so they overwrite any components imported so far (by routes also)
import './styles/global-overwrites.css';
import './styles/globals.css';

import { useAutomaticUpdates } from 'lincd-capacitor/hooks/useAutomaticUpdates';
import { UserAccount } from 'profile-plus/shapes/UserAccount';
import {
  DownloadOverlay,
  MaintenanceOverlay,
} from './components/atoms/Overlays.js';
import { TranslationProvider } from './utils/tolgee.js';

//load hardcoded data about topics and resources into memory both in backend and frontend
//import generatedData from '../data/filestores/generated.json';
import { Player } from 'lincd-irlcg/shapes/Player';
import { useEffect } from 'react';

if (typeof document !== 'undefined') {
  console.log('readyState:', document.readyState);
  document.addEventListener('DOMContentLoaded', () => {
    console.log('✅ DOMContentLoaded');
  });
  window.addEventListener('load', () => {
    console.log('✅ load');
  });
}

var locallyLoaded = [Player];
//JSONLD.parse(generatedData);

const firebaseConfig = {
  apiKey: process.env.FIREBASE_API_KEY,
  authDomain: process.env.FIREBASE_AUTH_DOMAIN,
  projectId: process.env.FIREBASE_PROJECT_ID,
  storageBucket: process.env.FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.FIREBASE_APP_ID,
  measurementId: process.env.FIREBASE_MEASUREMENT_ID,
};
export default function App() {
  // lazy initialize Firebase only when needed
  useEffect(() => {
    import('firebase/app').then(({ initializeApp }) => {
      initializeApp(firebaseConfig);
    });
  }, []);
  // Defensive: builtin bundle may have old capacitor package that returns undefined
  const updates = useAutomaticUpdates() as unknown as
    | {
        otaStatus?: string;
        otaProgress?: number;
        maintenanceMode?: boolean;
        retryOTA?: () => void;
        retryMaintenanceCheck?: () => void;
      }
    | undefined;
  const {
    otaStatus = 'idle',
    otaProgress = 0,
    maintenanceMode = false,
    retryOTA = () => {},
    retryMaintenanceCheck = () => {},
  } = updates ?? {};

  // show OTA overlay when downloading, showing "Update ready" before reload, or on download/apply error
  const showOTAOverlay =
    otaStatus === 'downloading' ||
    otaStatus === 'complete' ||
    otaStatus === 'error';

  // show maintenance overlay only when server is down and we're not already showing an OTA overlay
  const showMaintenanceOverlay = maintenanceMode && !showOTAOverlay;

  return (
    <AppRoot>
      <Head>
        {/*  Add tags to html <head> here, for example, a font <link href='https://fonts.someapi.com/...' />*/}
        {/* WHATEVER YOU ADD HERE NEEDS TO BE ADDED TO /web/index.html as well for ios/android */}
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"
        />
        <link
          href="
https://cdn.jsdelivr.net/npm/react-international-phone@4.0.4/build/index.min.css
"
          rel="stylesheet"
        />
        <link
          href="
https://cdn.jsdelivr.net/npm/react-date-picker@10.6.0/dist/DatePicker.min.css
"
          rel="stylesheet"
        />
        <link
          href="
https://cdn.jsdelivr.net/npm/react-calendar@4.8.0/dist/Calendar.min.css
"
          rel="stylesheet"
        />
      </Head>
      <Body routes={ROUTES} pageStyles={style} className={style.App}>
        <Suspense fallback={<Spinner centered={true} />}>
          <ErrorBoundary FallbackComponent={Error}>
            <ProvideAuth
              userType={Player}
              accountType={UserAccount}
              signinRoute={ROUTES.signin.path}
            >
              <TranslationProvider>
                <CapacitorProvider>
                  <AppRoutes />
                </CapacitorProvider>
              </TranslationProvider>
            </ProvideAuth>
          </ErrorBoundary>
        </Suspense>
        {showOTAOverlay && (
          <DownloadOverlay
            progress={otaProgress}
            status={
              otaStatus === 'error'
                ? 'error'
                : otaStatus === 'complete'
                  ? 'complete'
                  : 'downloading'
            }
            onRetry={otaStatus === 'error' ? retryOTA : undefined}
          />
        )}
        {showMaintenanceOverlay && (
          <MaintenanceOverlay onRetry={retryMaintenanceCheck} />
        )}
      </Body>
    </AppRoot>
  );
}
